import { transformError as sdkTransformError } from '@wix/sdk-runtime/transform-error';
import {
  renameKeysFromSDKRequestToRESTRequest,
  renameKeysFromRESTResponseToSDKResponse,
} from '@wix/sdk-runtime/rename-all-nested-keys';
import { HttpClient, NonNullablePaths } from '@wix/sdk-types';
import * as ambassadorWixPromoteMarketingV2AdsTxt from './promote-marketing-v2-ads-txt-ads-txt.http.js';

export interface AdsTxt {
  /**
   * Content of Ads.txt.
   * @maxLength 720000
   */
  content?: string;
  /** Whether current Ads.txt is Wix's default. */
  default?: boolean;
  /**
   * Subdomain of Ads.txt, for example `www`, `es`, `fr`. Default is `www`.
   * @maxLength 63
   */
  subdomain?: string;
  /**
   * entity Id
   * @internal
   */
  _id?: string;
}

/** The request to get the Ads.txt file content */
export interface GetAdsTxtRequest {
  /** Subdomain of Ads.txt, for example `www`, `es`, `fr`. Default is `www`. */
  subdomain?: string;
}

/** The response of the Ads.txt file request */
export interface GetAdsTxtResponse {
  /** Ads txt object */
  adsTxt?: AdsTxt;
}

/** The request to update the content of the Ads.txt file */
export interface UpdateAdsTxtRequest {
  /**
   * Submitted `adsTxT` object will replace the existing Ads.txt file.
   * To reset Ads.txt to Wix's default, submit `default:true` without a `content` object.
   */
  adsTxt?: AdsTxt;
}

export interface UpdateAdsTxtResponse {
  /** Updated Ads txt object */
  adsTxt?: AdsTxt;
}

/** The request to append the content of the Ads.txt file */
export interface AppendAdsTxtRequest {
  /**
   * Submitted `content` object will be appended to the existing Ads.txt file.
   * To reset Ads.txt to Wix's default, submit `default:true` without a `content` object.
   */
  adsTxt?: AdsTxt;
}

export interface AppendAdsTxtResponse {
  /** Appended Ads.txt file. */
  adsTxt?: AdsTxt;
}

export interface DomainEvent extends DomainEventBodyOneOf {
  createdEvent?: EntityCreatedEvent;
  updatedEvent?: EntityUpdatedEvent;
  deletedEvent?: EntityDeletedEvent;
  actionEvent?: ActionEvent;
  /** Event ID. With this ID you can easily spot duplicated events and ignore them. */
  _id?: string;
  /**
   * Fully Qualified Domain Name of an entity. This is a unique identifier assigned to the API main business entities.
   * For example, `wix.stores.catalog.product`, `wix.bookings.session`, `wix.payments.transaction`.
   */
  entityFqdn?: string;
  /**
   * Event action name, placed at the top level to make it easier for users to dispatch messages.
   * For example: `created`/`updated`/`deleted`/`started`/`completed`/`email_opened`.
   */
  slug?: string;
  /** ID of the entity associated with the event. */
  entityId?: string;
  /** Event timestamp in [ISO-8601](https://en.wikipedia.org/wiki/ISO_8601) format and UTC time. For example, `2020-04-26T13:57:50.699Z`. */
  eventTime?: Date | null;
  /**
   * Whether the event was triggered as a result of a privacy regulation application
   * (for example, GDPR).
   */
  triggeredByAnonymizeRequest?: boolean | null;
  /** If present, indicates the action that triggered the event. */
  originatedFrom?: string | null;
  /**
   * A sequence number that indicates the order of updates to an entity. For example, if an entity was updated at `16:00` and then again at `16:01`, the second update will always have a higher sequence number.
   * You can use this number to make sure you're handling updates in the right order. Just save the latest sequence number on your end and compare it to the one in each new message. If the new message has an older (lower) number, you can safely ignore it.
   */
  entityEventSequence?: string | null;
}

/** @oneof */
export interface DomainEventBodyOneOf {
  createdEvent?: EntityCreatedEvent;
  updatedEvent?: EntityUpdatedEvent;
  deletedEvent?: EntityDeletedEvent;
  actionEvent?: ActionEvent;
}

export interface EntityCreatedEvent {
  entity?: string;
}

export interface RestoreInfo {
  deletedDate?: Date | null;
}

export interface EntityUpdatedEvent {
  /**
   * Since platformized APIs only expose PATCH and not PUT we can't assume that the fields sent from the client are the actual diff.
   * This means that to generate a list of changed fields (as opposed to sent fields) one needs to traverse both objects.
   * We don't want to impose this on all developers and so we leave this traversal to the notification recipients which need it.
   */
  currentEntity?: string;
}

export interface EntityDeletedEvent {
  /** Entity that was deleted. */
  deletedEntity?: string | null;
}

export interface ActionEvent {
  body?: string;
}

export interface MessageEnvelope {
  /**
   * App instance ID.
   * @format GUID
   */
  instanceId?: string | null;
  /**
   * Event type.
   * @maxLength 150
   */
  eventType?: string;
  /** The identification type and identity data. */
  identity?: IdentificationData;
  /** Stringify payload. */
  data?: string;
  /** Details related to the account */
  accountInfo?: AccountInfo;
}

export interface IdentificationData extends IdentificationDataIdOneOf {
  /**
   * ID of a site visitor that has not logged in to the site.
   * @format GUID
   */
  anonymousVisitorId?: string;
  /**
   * ID of a site visitor that has logged in to the site.
   * @format GUID
   */
  memberId?: string;
  /**
   * ID of a Wix user (site owner, contributor, etc.).
   * @format GUID
   */
  wixUserId?: string;
  /**
   * ID of an app.
   * @format GUID
   */
  appId?: string;
  /** @readonly */
  identityType?: WebhookIdentityTypeWithLiterals;
}

/** @oneof */
export interface IdentificationDataIdOneOf {
  /**
   * ID of a site visitor that has not logged in to the site.
   * @format GUID
   */
  anonymousVisitorId?: string;
  /**
   * ID of a site visitor that has logged in to the site.
   * @format GUID
   */
  memberId?: string;
  /**
   * ID of a Wix user (site owner, contributor, etc.).
   * @format GUID
   */
  wixUserId?: string;
  /**
   * ID of an app.
   * @format GUID
   */
  appId?: string;
}

export enum WebhookIdentityType {
  UNKNOWN = 'UNKNOWN',
  ANONYMOUS_VISITOR = 'ANONYMOUS_VISITOR',
  MEMBER = 'MEMBER',
  WIX_USER = 'WIX_USER',
  APP = 'APP',
}

/** @enumType */
export type WebhookIdentityTypeWithLiterals =
  | WebhookIdentityType
  | 'UNKNOWN'
  | 'ANONYMOUS_VISITOR'
  | 'MEMBER'
  | 'WIX_USER'
  | 'APP';

export interface AccountInfo {
  /**
   * ID of the Wix account associated with the event.
   * @format GUID
   */
  accountId?: string | null;
  /**
   * ID of the parent Wix account. Only included when accountId belongs to a child account.
   * @format GUID
   */
  parentAccountId?: string | null;
  /**
   * ID of the Wix site associated with the event. Only included when the event is tied to a specific site.
   * @format GUID
   */
  siteId?: string | null;
}

export interface BaseEventMetadata {
  /**
   * App instance ID.
   * @format GUID
   */
  instanceId?: string | null;
  /**
   * Event type.
   * @maxLength 150
   */
  eventType?: string;
  /** The identification type and identity data. */
  identity?: IdentificationData;
  /** Details related to the account */
  accountInfo?: AccountInfo;
}

export interface EventMetadata extends BaseEventMetadata {
  /** Event ID. With this ID you can easily spot duplicated events and ignore them. */
  _id?: string;
  /**
   * Fully Qualified Domain Name of an entity. This is a unique identifier assigned to the API main business entities.
   * For example, `wix.stores.catalog.product`, `wix.bookings.session`, `wix.payments.transaction`.
   */
  entityFqdn?: string;
  /**
   * Event action name, placed at the top level to make it easier for users to dispatch messages.
   * For example: `created`/`updated`/`deleted`/`started`/`completed`/`email_opened`.
   */
  slug?: string;
  /** ID of the entity associated with the event. */
  entityId?: string;
  /** Event timestamp in [ISO-8601](https://en.wikipedia.org/wiki/ISO_8601) format and UTC time. For example, `2020-04-26T13:57:50.699Z`. */
  eventTime?: Date | null;
  /**
   * Whether the event was triggered as a result of a privacy regulation application
   * (for example, GDPR).
   */
  triggeredByAnonymizeRequest?: boolean | null;
  /** If present, indicates the action that triggered the event. */
  originatedFrom?: string | null;
  /**
   * A sequence number that indicates the order of updates to an entity. For example, if an entity was updated at `16:00` and then again at `16:01`, the second update will always have a higher sequence number.
   * You can use this number to make sure you're handling updates in the right order. Just save the latest sequence number on your end and compare it to the one in each new message. If the new message has an older (lower) number, you can safely ignore it.
   */
  entityEventSequence?: string | null;
  accountInfo?: AccountInfoMetadata;
}

export interface AccountInfoMetadata {
  /** ID of the Wix account associated with the event */
  accountId: string;
  /** ID of the Wix site associated with the event. Only included when the event is tied to a specific site. */
  siteId?: string;
  /** ID of the parent Wix account. Only included when 'accountId' belongs to a child account. */
  parentAccountId?: string;
}

export interface AdsTxtUpdatedEnvelope {
  entity: AdsTxt;
  metadata: EventMetadata;
}

/** @permissionScope Manage Ads.txt
 * @permissionScopeId SCOPE.DC-PROMOTE.MANAGE-ADS-TXT
 * @permissionId PROMOTE.ADS_TXT_WRITE
 * @webhook
 * @eventType wix.promote.marketing.ads_txt_updated
 * @slug updated
 */
export declare function onAdsTxtUpdated(
  handler: (event: AdsTxtUpdatedEnvelope) => void | Promise<void>
): void;

/**
 * Retrieves the Ads.txt file.
 * @public
 * @permissionId PROMOTE.ADS_TXT_READ
 * @applicableIdentity APP
 * @returns The response of the Ads.txt file request
 * @fqn com.wixpress.promote.seo.robots.server.v2.AdsServiceV2.GetAdsTxt
 */
export async function getAdsTxt(
  options?: GetAdsTxtOptions
): Promise<
  NonNullablePaths<
    GetAdsTxtResponse,
    `adsTxt.content` | `adsTxt.default` | `adsTxt.subdomain`,
    3
  >
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[1] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    subdomain: options?.subdomain,
  });

  const reqOpts = ambassadorWixPromoteMarketingV2AdsTxt.getAdsTxt(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data)!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: { subdomain: '$[0].subdomain' },
        singleArgumentUnchanged: false,
      },
      ['options']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

export interface GetAdsTxtOptions {
  /** Subdomain of Ads.txt, for example `www`, `es`, `fr`. Default is `www`. */
  subdomain?: string;
}

/**
 * Updates the Ads.txt file.
 * When setting the `content` object to an empty string, an empty Ads.txt file will be created.
 * In order to reset Ads.txt to Wix's default, send `default: true` without a `content` object.
 * @public
 * @permissionId PROMOTE.ADS_TXT_WRITE
 * @applicableIdentity APP
 * @fqn com.wixpress.promote.seo.robots.server.v2.AdsServiceV2.UpdateAdsTxt
 */
export async function updateAdsTxt(
  options?: UpdateAdsTxtOptions
): Promise<
  NonNullablePaths<
    UpdateAdsTxtResponse,
    `adsTxt.content` | `adsTxt.default` | `adsTxt.subdomain`,
    3
  >
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[1] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    adsTxt: options?.adsTxt,
  });

  const reqOpts = ambassadorWixPromoteMarketingV2AdsTxt.updateAdsTxt(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data)!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: { adsTxt: '$[0].adsTxt' },
        singleArgumentUnchanged: false,
      },
      ['options']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

export interface UpdateAdsTxtOptions {
  /**
   * Submitted `adsTxT` object will replace the existing Ads.txt file.
   * To reset Ads.txt to Wix's default, submit `default:true` without a `content` object.
   */
  adsTxt?: AdsTxt;
}

/**
 * Appends the submitted `content` object to Ads.txt.
 * In order to reset Ads.txt to Wix's default, send `default: true` without a `content` object.
 * @public
 * @permissionId PROMOTE.ADS_TXT_WRITE
 * @applicableIdentity APP
 * @fqn com.wixpress.promote.seo.robots.server.v2.AdsServiceV2.AppendAdsTxt
 */
export async function appendAdsTxt(
  options?: AppendAdsTxtOptions
): Promise<
  NonNullablePaths<
    AppendAdsTxtResponse,
    `adsTxt.content` | `adsTxt.default` | `adsTxt.subdomain`,
    3
  >
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[1] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    adsTxt: options?.adsTxt,
  });

  const reqOpts = ambassadorWixPromoteMarketingV2AdsTxt.appendAdsTxt(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data)!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: { adsTxt: '$[0].adsTxt' },
        singleArgumentUnchanged: false,
      },
      ['options']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

export interface AppendAdsTxtOptions {
  /**
   * Submitted `content` object will be appended to the existing Ads.txt file.
   * To reset Ads.txt to Wix's default, submit `default:true` without a `content` object.
   */
  adsTxt?: AdsTxt;
}
